/**
 * Runtime configuration helper
 *
 * In production (Docker), reads from window.__ENV__ which is generated
 * at container startup by docker-entrypoint.sh
 *
 * In development, falls back to import.meta.env (Vite's build-time env vars)
 */

declare global {
  interface Window {
    __ENV__?: {
      VITE_APPLICATION_BACKEND_HOST?: string
      VITE_APPLICATION_BACKEND_PORT?: string
      VITE_OIDC_AUTHORITY?: string
      VITE_OIDC_CLIENT_ID?: string
      VITE_OIDC_REDIRECT_URI?: string
      VITE_OIDC_SILENT_REDIRECT_URI?: string
      VITE_OIDC_POST_LOGOUT_REDIRECT_URI?: string
      VITE_DEBUG_LOGGING?: string
      VITE_DEBUG_FORM_VALUES?: string
      VITE_FORM_ENABLED_CUSTOMER?: string
      VITE_FORM_ENABLED_INVOICING?: string
      VITE_FORM_ENABLED_ORG?: string
      VITE_FORM_ENABLED_VAARATAPAHTUMA?: string
      VITE_FORM_ENABLED_KORJAUSPYYNTO?: string
      VITE_FORM_ENABLED_MUISTUTUS?: string
      VITE_FORM_DISABLED_REDIRECT?: string
    }
  }
}

/**
 * Get a configuration value, checking runtime config first, then build-time env
 */
function getEnv(key: string): string | undefined {
  // Check runtime config first (Docker production)
  const runtimeValue = window.__ENV__?.[key as keyof NonNullable<typeof window.__ENV__>]
  if (runtimeValue !== undefined && runtimeValue !== "") {
    return runtimeValue
  }

  // Fall back to build-time env vars (local development)
  return import.meta.env[key] as string | undefined
}

/**
 * Application configuration with runtime environment variable support
 */
export const config = {
  get backendHost(): string {
    const port = getEnv("VITE_APPLICATION_BACKEND_PORT") || "4000"
    return getEnv("VITE_APPLICATION_BACKEND_HOST") || `http://localhost:${port}`
  },

  get backendPort(): string {
    return getEnv("VITE_APPLICATION_BACKEND_PORT") || "4000"
  },

  get oidcAuthority(): string {
    return getEnv("VITE_OIDC_AUTHORITY") || "https://auth.test.terveystalo.com/oauth"
  },

  get oidcClientId(): string {
    return getEnv("VITE_OIDC_CLIENT_ID") || "jsmtest"
  },

  get oidcRedirectUri(): string {
    return getEnv("VITE_OIDC_REDIRECT_URI") || "https://localhost:3000"
  },

  get oidcSilentRedirectUri(): string | undefined {
    return getEnv("VITE_OIDC_SILENT_REDIRECT_URI")
  },

  get oidcPostLogoutRedirectUri(): string {
    return (
      getEnv("VITE_OIDC_POST_LOGOUT_REDIRECT_URI") ||
      getEnv("VITE_OIDC_REDIRECT_URI") ||
      "https://localhost:3000"
    )
  },

  get debugLogging(): boolean {
    return getEnv("VITE_DEBUG_LOGGING") === "true"
  },

  get debugFormValues(): boolean {
    return getEnv("VITE_DEBUG_FORM_VALUES") === "true"
  },

  /** Per-form enabled flags. Default: only invoicing is enabled. */
  get formEnabledCustomer(): boolean {
    return getEnv("VITE_FORM_ENABLED_CUSTOMER") === "true"
  },
  get formEnabledInvoicing(): boolean {
    const v = getEnv("VITE_FORM_ENABLED_INVOICING")
    return v === undefined || v === "" ? true : v === "true"
  },
  get formEnabledOrg(): boolean {
    return getEnv("VITE_FORM_ENABLED_ORG") === "true"
  },
  get formEnabledVaaratapahtuma(): boolean {
    return getEnv("VITE_FORM_ENABLED_VAARATAPAHTUMA") === "true"
  },
  get formEnabledKorjauspyynto(): boolean {
    return getEnv("VITE_FORM_ENABLED_KORJAUSPYYNTO") === "true"
  },
  get formEnabledMuistutus(): boolean {
    return getEnv("VITE_FORM_ENABLED_MUISTUTUS") === "true"
  },

  /**
   * Route key to redirect to when a user tries to open a disabled form.
   * Must be one of: customer, invoicing, org, vaaratapahtuma, korjauspyynto, muistutus.
   * Defaults to "invoicing".
   */
  get formDisabledRedirect(): string {
    return getEnv("VITE_FORM_DISABLED_REDIRECT") || "invoicing"
  },
}

export type FormRouteKey = "customer" | "invoicing" | "org" | "vaaratapahtuma" | "korjauspyynto" | "muistutus"

/** Check whether a form identified by its route key is enabled */
export function isFormEnabled(key: FormRouteKey): boolean {
  const map: Record<FormRouteKey, boolean> = {
    customer: config.formEnabledCustomer,
    invoicing: config.formEnabledInvoicing,
    org: config.formEnabledOrg,
    vaaratapahtuma: config.formEnabledVaaratapahtuma,
    korjauspyynto: config.formEnabledKorjauspyynto,
    muistutus: config.formEnabledMuistutus,
  }
  return map[key]
}

export default config
